import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ScraperRow, VendorFilter } from "@/components/operator/scraper-row";
import { useInboxBatchExtraction } from "@/hooks/useInboxExtraction";
import { useOperatorStatus } from "@/hooks/useOperatorStatus";
import {
  useLinkSourceVendorPage,
  useRunSource,
  useSources,
  useSyncContentScraperSource,
} from "@/hooks/useSources";
import { useVendorPages } from "@/hooks/useVendorPages";
import { ApiError, type SourceConfig, type VendorPage } from "@/lib/api";
import { buildScraperRows, groupScraperRows } from "@/lib/scraper-catalog";

/** Scraper cockpit: vendor pages + scraper configs + worker state in one operator view. */
export default function OperatorScraperPage() {
  const vendorPagesQ = useVendorPages();
  const sourcesQ = useSources();
  const statusQ = useOperatorStatus();
  const runSource = useRunSource();
  const syncSource = useSyncContentScraperSource();
  const linkSourceVendorPage = useLinkSourceVendorPage();
  const analyzeSource = useInboxBatchExtraction();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runningSourceId, setRunningSourceId] = useState<string | null>(null);
  const [syncingSourceId, setSyncingSourceId] = useState<string | null>(null);
  const [analyzingSourceId, setAnalyzingSourceId] = useState<string | null>(null);
  const [linkingRowKey, setLinkingRowKey] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      buildScraperRows(
        vendorPagesQ.data ?? [],
        sourcesQ.data ?? [],
        statusQ.data?.source_states ?? [],
      ),
    [vendorPagesQ.data, sourcesQ.data, statusQ.data?.source_states],
  );
  const groupedRows = useMemo(() => groupScraperRows(rows), [rows]);
  const linkCandidatesByVendor = useMemo(() => {
    const candidates = new Map<string, SourceConfig[]>();
    for (const row of rows) {
      if (row.page || !row.source || row.source.is_active === false) continue;
      const bucket = candidates.get(row.vendorKey) ?? [];
      bucket.push(row.source);
      candidates.set(row.vendorKey, bucket);
    }
    return candidates;
  }, [rows]);
  const selectedVendor = searchParams.get("vendor") ?? "all";
  const visibleGroups = useMemo(
    () =>
      selectedVendor === "all"
        ? groupedRows
        : groupedRows.filter((group) => group.vendorKey === selectedVendor),
    [groupedRows, selectedVendor],
  );

  const runNow = async (source: SourceConfig) => {
    setRunningSourceId(source.id);
    try {
      const result = await runSource.mutateAsync(source.id);
      const captured = `${result.inbox_items_created} item${result.inbox_items_created === 1 ? "" : "s"}`;
      const processing =
        result.processing_status === "skipped"
          ? "processing skipped"
          : `${result.processing_status}: ${result.processing_converted_count} converted`;
      toast.success(`${source.display_name}: captured ${captured}; ${processing}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.info(`${source.display_name} is already running.`);
      } else {
        toast.error(`run failed: ${(err as Error).message}`);
      }
    } finally {
      setRunningSourceId(null);
    }
  };

  const syncWorker = async (source: SourceConfig) => {
    setSyncingSourceId(source.id);
    try {
      const result = await syncSource.mutateAsync(source.id);
      toast.success(result.message);
    } catch (err) {
      toast.error(`sync failed: ${(err as Error).message}`);
    } finally {
      setSyncingSourceId(null);
    }
  };

  const analyzeExisting = (source: SourceConfig) => {
    const processing = processingFromSource(source);
    setAnalyzingSourceId(source.id);
    analyzeSource.mutate(
      {
        source_id: [source.id],
        status: "new",
        only_unstructured: true,
        group_size: processing.group_size,
        limit: 100,
      },
      {
        onSettled: () => setAnalyzingSourceId(null),
      },
    );
  };

  const linkVendorPage = async (source: SourceConfig, page: VendorPage, rowKey: string) => {
    setLinkingRowKey(rowKey);
    try {
      await linkSourceVendorPage.mutateAsync({
        sourceId: source.id,
        vendorPageId: page.id,
      });
      toast.success(`${source.display_name} linked to ${page.label}`);
    } catch (err) {
      toast.error(`link failed: ${(err as Error).message}`);
    } finally {
      setLinkingRowKey(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border/70 px-6 py-4">
        <h1 className="text-xl font-semibold">Content Scraper</h1>
        <p className="text-xs text-muted-fg">
          Vendor pages, scraper configs, worker state, and capture actions.
        </p>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {vendorPagesQ.isLoading || sourcesQ.isLoading || statusQ.isLoading ? (
          <div className="text-sm text-muted-fg">Loading scraping configuration…</div>
        ) : vendorPagesQ.isError || sourcesQ.isError || statusQ.isError ? (
          <div className="text-sm text-red-400">Failed to load scraping configuration.</div>
        ) : groupedRows.length === 0 ? (
          <div className="text-sm text-muted-fg">No vendor pages or scraper configs found.</div>
        ) : (
          <div className="space-y-6">
            <VendorFilter
              groups={groupedRows}
              selectedVendor={selectedVendor}
              onSelect={(vendor) => {
                if (vendor === "all") {
                  setSearchParams({});
                } else {
                  setSearchParams({ vendor });
                }
              }}
            />
            {visibleGroups.map((group) => (
              <section key={group.vendorKey} className="space-y-2">
                <div className="flex items-center justify-between">
                  <a
                    href={`/operator/scraper?vendor=${encodeURIComponent(group.vendorKey)}`}
                    className="text-sm font-semibold text-fg hover:text-accent hover:underline"
                  >
                    {group.vendorLabel}
                  </a>
                  <span className="text-xs text-muted-fg">
                    {group.rows.length} page{group.rows.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="overflow-hidden rounded-md border border-border/70">
                  {group.rows.map((row) => (
                    <ScraperRow
                      key={row.key}
                      row={row}
                      running={runningSourceId === row.source?.id}
                      syncing={syncingSourceId === row.source?.id}
                      analyzing={analyzingSourceId === row.source?.id}
                      linking={linkingRowKey === row.key}
                      linkCandidates={linkCandidatesByVendor.get(row.vendorKey) ?? []}
                      onRun={runNow}
                      onSync={syncWorker}
                      onAnalyze={analyzeExisting}
                      onLinkVendorPage={(source, page) => linkVendorPage(source, page, row.key)}
                    />
                  ))}
                </div>
              </section>
            ))}
            {visibleGroups.length === 0 ? (
              <div className="rounded-md border border-border/70 px-3 py-8 text-center text-sm text-muted-fg">
                No scraping rows for this vendor.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function processingFromSource(source: SourceConfig): { auto_extract: boolean; group_size: number } {
  const processing = source.config?.processing;
  if (!isRecord(processing)) return { auto_extract: false, group_size: 1 };
  return {
    auto_extract: processing.auto_extract === true,
    group_size: boundedNumber(processing.group_size, 1, 1, 20),
  };
}

function boundedNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, min), max);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
